import { BAYS } from '../../../constants';

import type { TFunction } from 'i18next';
import type { MUIDataTableOptions } from 'mui-datatables';
import type {
  ClientSignsSymptomsChecklistField,
  EvidenceBasedInterventionsChecklistField,
  SessionChecklistOption,
} from './types';

export const RateTutorialVideoLink =
  'https://cdn.prod.fortahealth.com/videos/Rate_in_Session_Notes.mp4';

export const DURATION_PLACEHOLDER_VALUE = 'select-duration';

export const getEvidencedBasedInterventionsData = (
  t: TFunction<'sessionNotes'>,
): SessionChecklistOption<EvidenceBasedInterventionsChecklistField>[] => [
  {
    id: 'antecedent_based_interventions',
    label: t('evidenceBasedInterventionsUsed.fields.antecedentBasedInterventions'),
    columnNumber: 1,
    description: t('evidenceBasedInterventionsUsed.tooltip.antecedentBasedInterventions'),
  },
  {
    id: 'cognitive_behavioral_intervention',
    label: t('evidenceBasedInterventionsUsed.fields.cognitiveBehavioralIntervention'),
    columnNumber: 1,
    description: t('evidenceBasedInterventionsUsed.tooltip.cognitiveBehavioralIntervention'),
  },
  {
    id: 'differential_reinforcement_of_alternative_incompatible_or_other_behaviors',
    label: t(
      'evidenceBasedInterventionsUsed.fields.differentialReinforcementOfAlternativeIncompatibleOrOtherBehaviors',
    ),
    columnNumber: 1,
    description: t(
      'evidenceBasedInterventionsUsed.tooltip.differentialReinforcementOfAlternativeIncompatibleOrOtherBehaviors',
    ),
  },
  {
    id: 'discrete_trial_teaching',
    label: t('evidenceBasedInterventionsUsed.fields.discreteTrialTeaching'),
    columnNumber: 1,
    description: t('evidenceBasedInterventionsUsed.tooltip.discreteTrialTeaching'),
  },
  {
    id: 'functional_behavior_assessment',
    label: t('evidenceBasedInterventionsUsed.fields.functionalBehaviorAssessment'),
    columnNumber: 1,
    description: t('evidenceBasedInterventionsUsed.tooltip.functionalBehaviorAssessment'),
  },
  {
    id: 'functional_communication_training',
    label: t('evidenceBasedInterventionsUsed.fields.functionalCommunicationTraining'),
    columnNumber: 2,
    description: t('evidenceBasedInterventionsUsed.tooltip.functionalCommunicationTraining'),
  },
  {
    id: 'modeling',
    label: t('evidenceBasedInterventionsUsed.fields.modeling'),
    columnNumber: 2,
    description: t('evidenceBasedInterventionsUsed.tooltip.modeling'),
  },
  {
    id: 'naturalistic_intervention',
    label: t('evidenceBasedInterventionsUsed.fields.naturalisticIntervention'),
    columnNumber: 2,
    description: t('evidenceBasedInterventionsUsed.tooltip.naturalisticIntervention'),
  },
  {
    id: 'parent_implemented_intervention',
    label: t('evidenceBasedInterventionsUsed.fields.parentImplementedIntervention'),
    columnNumber: 2,
    description: t('evidenceBasedInterventionsUsed.tooltip.parentImplementedIntervention'),
  },
  {
    id: 'peer_mediated_instruction_and_intervention',
    label: t('evidenceBasedInterventionsUsed.fields.peerMediatedInstructionAndIntervention'),
    columnNumber: 2,
    description: t('evidenceBasedInterventionsUsed.tooltip.peerMediatedInstructionAndIntervention'),
  },
  {
    id: 'picture_exchange_communication_systems',
    label: t('evidenceBasedInterventionsUsed.fields.pictureExchangeCommunicationSystems'),
    columnNumber: 3,
    description: t('evidenceBasedInterventionsUsed.tooltip.pictureExchangeCommunicationSystems'),
  },
  {
    id: 'pivotal_response_training',
    label: t('evidenceBasedInterventionsUsed.fields.pivotalResponseTraining'),
    columnNumber: 3,
    description: t('evidenceBasedInterventionsUsed.tooltip.pivotalResponseTraining'),
  },
  {
    id: 'prompting',
    label: t('evidenceBasedInterventionsUsed.fields.prompting'),
    columnNumber: 3,
    description: t('evidenceBasedInterventionsUsed.tooltip.prompting'),
  },
  {
    id: 'reinforcement',
    label: t('evidenceBasedInterventionsUsed.fields.reinforcement'),
    columnNumber: 3,
    description: t('evidenceBasedInterventionsUsed.tooltip.reinforcement'),
  },
];

export const getClientSignsSymptomsData = (
  t: TFunction<'sessionNotes'>,
): SessionChecklistOption<ClientSignsSymptomsChecklistField>[] => [
  {
    id: 'adaptive_skills_deficit',
    label: t('symptomsObserved.fields.adaptiveSkillsDeficit'),
    columnNumber: 1,
    description: t('symptomsObserved.tooltip.adaptiveSkillsDeficit'),
  },
  {
    id: 'cognitive_skills_deficit',
    label: t('symptomsObserved.fields.cognitiveSkillsDeficit'),
    columnNumber: 1,
    description: t('symptomsObserved.tooltip.cognitiveSkillsDeficit'),
  },
  {
    id: 'community_safety_skills_deficit',
    label: t('symptomsObserved.fields.communitySafetySkillsDeficit'),
    columnNumber: 1,
    description: t('symptomsObserved.tooltip.communitySafetySkillsDeficit'),
  },
  {
    id: 'expressive_receptive_communication_deficit',
    label: t('symptomsObserved.fields.expressiveReceptiveCommunicationDeficit'),
    columnNumber: 1,
    description: t('symptomsObserved.tooltip.expressiveReceptiveCommunicationDeficit'),
  },
  {
    id: 'joint_attention_skills_deficit',
    label: t('symptomsObserved.fields.jointAttentionSkillsDeficit'),
    columnNumber: 1,
    description: t('symptomsObserved.tooltip.jointAttentionSkillsDeficit'),
  },
  {
    id: 'maladaptive_behaviors',
    label: t('symptomsObserved.fields.maladaptiveBehaviors'),
    columnNumber: 2,
    description: t('symptomsObserved.tooltip.maladaptiveBehaviors'),
  },
  {
    id: 'motor_skills_deficit',
    label: t('symptomsObserved.fields.motorSkillsDeficit'),
    columnNumber: 2,
    description: t('symptomsObserved.tooltip.motorSkillsDeficit'),
  },
  {
    id: 'nonverbal_communication_deficit',
    label: t('symptomsObserved.fields.nonVerbalCommunicationDeficit'),
    columnNumber: 2,
    description: t('symptomsObserved.tooltip.nonVerbalCommunicationDeficit'),
  },
  {
    id: 'play_skills_deficit',
    label: t('symptomsObserved.fields.playSkillsDeficit'),
    columnNumber: 2,
    description: t('symptomsObserved.tooltip.playSkillsDeficit'),
  },
  {
    id: 'restricted_repetitive_behaviors',
    label: t('symptomsObserved.fields.restrictedRepetitiveBehaviors'),
    columnNumber: 2,
    description: t('symptomsObserved.tooltip.restrictedRepetitiveBehaviors'),
  },
  {
    id: 'school_readiness_skills_deficit',
    label: t('symptomsObserved.fields.schoolReadinessSkillsDeficit'),
    columnNumber: 3,
    description: t('symptomsObserved.tooltip.schoolReadinessSkillsDeficit'),
  },
  {
    id: 'social_communication_deficit',
    label: t('symptomsObserved.fields.socialCommunicationDeficits'),
    columnNumber: 3,
    description: t('symptomsObserved.tooltip.socialCommunicationDeficits'),
  },
  {
    id: 'social_interaction_deficit',
    label: t('symptomsObserved.fields.socialInteractionDeficit'),
    columnNumber: 3,
    description: t('symptomsObserved.tooltip.socialInteractionDeficit'),
  },
  {
    id: 'social_emotional_reciprocity_deficit',
    label: t('symptomsObserved.fields.socialEmotionalReciprocityDeficits'),
    columnNumber: 3,
    description: t('symptomsObserved.tooltip.socialEmotionalReciprocityDeficits'),
  },
];

export const segmentSliderInitialState = {
  client_progress: '',
  synopsis: {
    client_adheres_with_targets_or_goals: false,
    no_behavior_issues_or_family_changes: false,
  },
  narrative: '',
  signs_and_symptoms_observed: {
    other: '',
    play_skills_deficit: false,
    motor_skills_deficit: false,
    maladaptive_behaviors: false,
    adaptive_skills_deficit: false,
    cognitive_skills_deficit: false,
    social_interaction_deficit: false,
    social_communication_deficit: false,
    joint_attention_skills_deficit: false,
    community_safety_skills_deficit: false,
    restricted_repetitive_behaviors: null,
    restricted_receptive_behaviors: null,
    school_readiness_skills_deficit: false,
    nonverbal_communication_deficit: false,
    social_emotional_reciprocity_deficit: false,
    expressive_receptive_communication_deficit: false,
  },
  evidence_based_interventions_used: {
    other: '',
    modeling: false,
    prompting: false,
    reinforcement: false,
    discrete_trial_teaching: false,
    naturalistic_intervention: false,
    pivotal_response_training: false,
    antecedent_based_interventions: false,
    parent_implemented_intervention: false,
    cognitive_behavioral_intervention: false,
    functional_communication_training: false,
    picture_exchange_communication_systems: false,
    functional_behavior_assessment: false,
    peer_mediated_instruction_and_intervention: false,
    differential_reinforcement_of_alternative_incompatible_or_other_behaviors: false,
  },
};

export const Durations = {
  '0:30 hr': 1800,
  '0:45 hr': 2700,
  '1:00 hr': 3600,
  '1:15 hr': 4500,
  '1:30 hr': 5400,
  '1:45 hr': 6300,
  '2:00 hr': 7200,
  '2:15 hr': 8100,
  '2:30 hr': 9000,
  '2:45 hr': 9900,
  '3:00 hr': 10800,
  '3:15 hr': 11700,
  '3:30 hr': 12600,
  '3:45 hr': 13500,
  '4:00 hr': 14400,
};

export const DurationOptions = {
  '0:15 hr': 900,
  ...Durations,
};

export const DEFAULT_SESSION_DURATION = Durations['3:00 hr'];

export enum Locations {
  home = 'Home',
  community = 'Community',
  school = 'School',
  community_settings = 'Community settings',
}

export enum newLocations {
  home = 'Home',
  community_settings = 'Community settings',
}

export enum DeliveryMethods {
  IN_PERSON = 'In-person',
  TELEHEALTH = 'Telehealth',
}

export enum SessionNoteSteps {
  SymptomsAndInterventions = 0,
  GoalsProgress = 1,
  SessionNarrative = 2,
}

export const NARRATIVE_AI_ALLOWED_FAILURE_COUNT = 3;

export const legacyGoalsColumns = [
  {
    name: 'behavior',
    label: 'Focus area',
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: 'goal',
    label: 'Goal',
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: 'settings',
    label: 'Setting',
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: 'attempts',
    label: 'Instances',
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: 'success',
    label: 'Successes',
    options: {
      filter: false,
      sort: false,
    },
  },
];

export const legacyGoalsTableOptions = {
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: false,
  fixedHeader: false,
  sort: true,
  download: false,
  responsive: 'standard',
  selectableRows: 'none',
  textLabels: {
    body: {
      noMatch: 'No goals',
    },
  },
} satisfies MUIDataTableOptions;

export const GUARDIAN_VERIFICATION_TOKEN_LIFESPAN_MS = 300000; // 5 minutes

export const TELEHEALTH_OPTION = {
  value: DeliveryMethods.TELEHEALTH,
  label: DeliveryMethods.TELEHEALTH,
};
export const IN_PERSON_OPTION = {
  value: DeliveryMethods.IN_PERSON,
  label: DeliveryMethods.IN_PERSON,
};

export const BCBA_ALLOWED_53_NOTES_BAYS = [BAYS.BAY_2_6, BAYS.BAY_6, BAYS.BAY_7];

export const SESSION_NOTE_VERSIONS = {
  V1: 'v1',
  V2: 'v2',
} as const;

export const SESSION_BREAK_MIN_DURATION_SECONDS = 7 * 60;
export const SESSION_BREAK_MAX_DURATION_SECONDS = 22 * 60;
